import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import Messages from '../../assets/locales/pt/translation.json'

export enum SnackBarMessageType {
    ERROR = 'error',
    INFO = 'info',
    WARNING = 'warning',
    SUCCESS = 'success'
}

export interface SnackBarState {
    readonly open: boolean
    readonly message: string
    readonly title: string
    readonly type: SnackBarMessageType
}

export interface DialogState {
    readonly open: boolean
    readonly message: string
    readonly title: string
    readonly to?: string
}

export interface LayoutState {
    readonly snackBar: SnackBarState
    readonly dialog: DialogState
}

export interface IActionsOpenSnackBar {
    readonly type: SnackBarMessageType
    readonly title: string
    readonly message: string
}

export interface IActionsOpenDialog {
    readonly title: string
    readonly message: string
    readonly to?: string
}

const initialState: LayoutState = {
    snackBar: {
        open: false,
        title: '',
        message: '',
        type: SnackBarMessageType.INFO
    },
    dialog: {
        open: false,
        title: '',
        message: '',
        to: ''
    }
}

export const snackbarSlice = createSlice({
    name: '@snackbar',
    initialState,
    reducers: {
        openSnackBar: (state: Draft<LayoutState>, action: PayloadAction<IActionsOpenSnackBar>) => {
            const { title, type, message } = action.payload
            state.snackBar.open = true
            state.snackBar.title = title
            state.snackBar.type = type
            state.snackBar.message = message
        },
        closeSnackBar: (state: Draft<LayoutState>) => {
            state.snackBar.open = initialState.snackBar.open
            state.snackBar.title = initialState.snackBar.title
            state.snackBar.message = initialState.snackBar.message
            state.snackBar.type = initialState.snackBar.type
        },
        openSnackBarInternalError: (state: Draft<LayoutState>) => {
            state.snackBar.open = true
            state.snackBar.title = 'Erro Interno.'
            state.snackBar.type = SnackBarMessageType.ERROR
            state.snackBar.message = Messages.REQUEST.PERSON.REQUEST.INTERNAL
        },
        openDialog: (state: Draft<LayoutState>, action: PayloadAction<IActionsOpenDialog>) => {
            const { title, message, to } = action.payload
            state.dialog.open = true
            state.dialog.title = title
            state.dialog.message = message
            state.dialog.to = to
        },
        onCloseDialog: (state: Draft<LayoutState>) => {
            state.dialog.open = initialState.dialog.open
            state.dialog.title = initialState.dialog.title
            state.dialog.message = initialState.dialog.message
            state.dialog.to = initialState.dialog.to
        },
    }
})

/**
 * <h5>Actions used to manipulate the application's layout.</h5>
 * @see {@link https://redux.js.org/tutorials/fundamentals/part-3-state-actions-reducers#designing-actions}
 * @typedef SnackbarActions
 * @namespace SnackbarActions
 * @category React
 * @subcategory Redux / Actions
 *
 * @property {Function} open
 * @property {Function} close
 */
export const SnackbarActions = snackbarSlice.actions

export default snackbarSlice.reducer

